// frontend/src/pages/Page.tsx - MODIFICATIONS POUR PROTÉGER LES PAGES

import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useContext, useEffect, useState } from 'react';

import { sideViewState, useAuth, useConfig, ChainlitContext } from '@chainlit/react-client';

import ElementSideView from '@/components/ElementSideView';
import LeftSidebar from '@/components/LeftSidebar';
import { TaskList } from '@/components/Tasklist';
import { Header } from '@/components/header';
import { ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { Loader } from '@/components/Loader';

import { userEnvState } from 'state/user';

import { isAdminConsole } from '@/AppMode';

type Props = {
  children: JSX.Element;
};

const Page = ({ children }: Props) => {
  const { config } = useConfig();
  const { data, user } = useAuth();
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Back-office : pas d'historique des discussions ni de panneau latéral du chat
  const isBackOffice =
    isAdminConsole() || pathname.startsWith('/admin') || pathname.startsWith('/users');
  const userEnv = useRecoilValue(userEnvState);
  const sideView = useRecoilValue(sideViewState);
  
  // ✅ NOUVEAU : État pour vérifier first_login
  const [checkingFirstLogin, setCheckingFirstLogin] = useState(true);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);

  // ✅ NOUVEAU : Vérifier first_login au chargement
  useEffect(() => {
    const checkFirstLogin = async () => {
      if (!user) {
        setCheckingFirstLogin(false);
        return;
      }

      try {
        const response = await fetch(
          apiClient.buildEndpoint('/api/auth/me'),
          {
            method: 'GET',
            credentials: 'include'
          }
        );

        if (response.ok) {
          const userData = await response.json();
          
          if (userData.first_login === true) {
            console.log('🔐 First login detected in Page, redirecting...');
            setNeedsPasswordChange(true);
            navigate('/change-password');
          }
        }
      } catch (err) {
        console.error('Error checking first login:', err);
      } finally {
        setCheckingFirstLogin(false);
      }
    };

    checkFirstLogin();
  }, [user, apiClient, navigate]);

  // ✅ Afficher loader pendant la vérification
  if (checkingFirstLogin) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="!size-8" />
      </div>
    );
  }

  // ✅ Si besoin de changer le mot de passe, ne rien afficher (redirection en cours)
  if (needsPasswordChange) {
    return null;
  }

  // Vérification des variables d'environnement (logique existante)
  if (config?.userEnv) {
    for (const key of config.userEnv || []) {
      if (!userEnv[key]) return <Navigate to="/env" />;
    }
  }

  const mainContent = (
    <div className="flex flex-col h-full w-full">
      <Header />
      <ResizablePanelGroup
        direction="horizontal"
        className="flex flex-row flex-grow"
      >
        <ResizablePanel
          className="flex flex-col h-full w-full"
          minSize={40}
          defaultSize={60}
        >
          <div className="flex flex-row flex-grow overflow-auto">
            {children}
          </div>
        </ResizablePanel>
        {isBackOffice ? null : sideView ? <ElementSideView /> : <TaskList isMobile={false} />}
      </ResizablePanelGroup>
    </div>
  );

  const historyEnabled = config?.dataPersistence && data?.requireLogin && !isBackOffice;

  return (
    <SidebarProvider
      defaultOpen={config?.ui.default_sidebar_state !== 'closed'}
    >
      {historyEnabled ? (
        <>
          <LeftSidebar />
          <SidebarInset className="max-h-svh">{mainContent}</SidebarInset>
        </>
      ) : (
        <div className="h-screen w-screen flex">{mainContent}</div>
      )}
    </SidebarProvider>
  );
};

export default Page;